import { Effect, Equal, Option, Result, Schema as S } from "effect";
import * as AsyncData from "foldkit/asyncData";
import * as Command from "foldkit/command";
import * as Navigation from "foldkit/navigation";
import { settle, start } from "../../api/asyncData";
import { asOfTag } from "../../api/client";
import {
	createBillingPortal,
	createCheckout,
	getSubscription,
} from "../../api/commands/billing";
import { toMessage } from "../../api/errors";
import type { Subscription } from "../../api/schemas/billing";
import { asOfOf } from "../../app/routes";
import type { Page, PageReturn, Shared } from "../shared";
import { Message } from "./message";
import type { Model } from "./model";
export const FetchSubscription = getSubscription.command(
	Message.CompletedFetch,
	asOfTag,
);
export const Checkout = createCheckout.command(
	Message.CompletedCheckout,
	asOfTag,
);
export const Portal = createBillingPortal.command(
	Message.CompletedPortal,
	asOfTag,
);
export const Redirect = Command.define("RedirectToBilling", {
	args: { url: S.String },
	messages: [Message.CompletedRedirect],
	execute: ({ url }) =>
		Navigation.load(url).pipe(Effect.as(Message.CompletedRedirect())),
});
const tag = (shared: Shared) => ({
	asOf: shared.asOf,
	generation: shared.generation,
});
const current = (
	shared: Shared,
	message: { asOf: Option.Option<string>; generation: number },
) =>
	shared.generation === message.generation &&
	Equal.equals(shared.asOf, message.asOf);
const load = (model: Model, shared: Shared): PageReturn<Model, Message> => ({
	model: { ...model, subscription: start(model.subscription) },
	commands: [FetchSubscription(tag(shared))],
});
export const canAct = (model: Model, shared: Shared): boolean =>
	!model.busy &&
	Option.isNone(shared.asOf) &&
	AsyncData.isSuccess(model.subscription) &&
	model.subscription.data.canManage;
export const canSubscribe = (subscription: Subscription): boolean =>
	!subscription.paid &&
	["free", "pending", "canceled", "expired"].includes(subscription.status);
export const page: Page<Model, Message> = {
	init: (_route, shared) =>
		load(
			{
				page: "CreditUsage",
				subscription: AsyncData.Idle(),
				busy: false,
				error: Option.none(),
			},
			shared,
		),
	changedRoute: (model, previous, next, shared) =>
		Equal.equals(asOfOf(previous), asOfOf(next))
			? { model }
			: load({ ...model, busy: false, error: Option.none() }, shared),
	update: (model, message, shared) => {
		const complete = (
			message: Extract<
				Message,
				{ _tag: "CompletedCheckout" | "CompletedPortal" }
			>,
		): PageReturn<Model, Message> => {
			if (!current(shared, message) || Option.isSome(shared.asOf))
				return { model };
			return Result.match(message.result, {
				onSuccess: ({ url }) => ({ model, commands: [Redirect({ url })] }),
				onFailure: (error) => ({
					model: {
						...model,
						busy: false,
						error: Option.some(toMessage(error)),
					},
				}),
			});
		};
		return Message.match<PageReturn<Model, Message>>(message, {
			CompletedFetch: (result) => ({
				model: current(shared, result)
					? {
							...model,
							subscription: settle(model.subscription, result.result),
						}
					: model,
			}),
			ClickedSubscribe: () =>
				canAct(model, shared) &&
				AsyncData.isSuccess(model.subscription) &&
				canSubscribe(model.subscription.data) &&
				model.subscription.data.enabled
					? {
							model: { ...model, busy: true, error: Option.none() },
							commands: [Checkout(tag(shared))],
						}
					: { model },
			ClickedPortal: () =>
				canAct(model, shared) &&
				AsyncData.isSuccess(model.subscription) &&
				model.subscription.data.canOpenPortal
					? {
							model: { ...model, busy: true, error: Option.none() },
							commands: [Portal(tag(shared))],
						}
					: { model },
			CompletedCheckout: (result) => complete(result),
			CompletedPortal: (result) => complete(result),
			CompletedRedirect: () => ({ model }),
		});
	},
};
